import type { ReactNode } from "react";
import type { GuardrailPerCorner, StudyEvaluation } from "../../api/evaluation";
import { guardrailVerdict } from "../../api/evaluation";
import type { ProvenanceInputStatus } from "../../api/tests";
import type { StudyInputsResponse } from "../../api/studyInputs";
import type { Test } from "../../api/tests";
import { InputCard } from "./InputCard";
import { tireOverrideOf } from "./tireOverride";
import {
  rigRows, platformRows, tyreRows, damperRows, type DetailRow,
} from "./studyInputsView";

export type StudyDefinitionRailProps = {
  test: Test;
  // Slice 2: the consolidated study evaluation (objective + per-run
  // guardrails). Optional + nullable — a failed evaluation fetch must
  // not break the rail, so callers pass null and we fall back to the
  // pre-existing placeholder/trust-only rendering.
  evaluation?: StudyEvaluation | null;
  // The four-input audit detail (from the Reference run). Null/pending until
  // R0 completes — cards then degrade to their headline value only.
  inputs?: StudyInputsResponse | null;
};

function trackOf(test: Test): string { return test.track_code; }

// Phase 4: the REQUESTED rig-actuation source rides on kpi_targets.track_replay
// and is known at create time — surface it on the rail immediately, before the
// Reference run resolves the effective source. Mirrors `tireOverrideOf`.
function requestedActuationLabel(test: Test): string {
  const tr = (test.kpi_targets as Record<string, unknown> | undefined)
    ?.track_replay as { enabled?: boolean } | undefined;
  return tr?.enabled ? "track replay" : "cached seven-post";
}

// Fable P4: an own-setup replay Study has no source_event — the staged
// setup id rides on kpi_targets.setup_file ("<season>/<staged name>").
function setupFileOf(test: Test): string | null {
  const sf = (test.kpi_targets as Record<string, unknown> | undefined)
    ?.setup_file;
  return typeof sf === "string" && sf ? sf : null;
}

// Compact display label for a staged-setup id: the engineer-authored
// filename without the "<season>/<event>__<car>__" staging prefix.
function setupFileLabel(setupFile: string): string {
  const name = setupFile.split("/").pop() ?? setupFile;
  const last = name.split("__").pop() ?? name;
  return last.replace(/\.xlsm$/i, "").replace(/_/g, " ");
}

// Render a card's audit rows behind a native disclosure so the context band
// stays compact — the headline value shows always, the full engineering detail
// (with provenance source dimmed) is one click away. Returns null when empty.
function detailBlock(rows: DetailRow[], testid: string): ReactNode {
  if (!rows.length) return null;
  return (
    <details className="study-card-disclosure">
      <summary className="study-card-disclosure-summary">details</summary>
      <div className="study-detail-rows" data-testid={testid}>
        {rows.map((r, i) => (
          <div className="study-detail-row" key={i}>
            <span className="study-detail-label">{r.label}</span>
            <span className="study-detail-value">{r.value}</span>
            {r.source && <span className="study-detail-src">{r.source}</span>}
          </div>
        ))}
      </div>
    </details>
  );
}

// ↓ for a minimise objective, ↑ for maximise. "target" objectives have
// no single better direction, so we show a neutral marker.
function directionMarker(direction: "min" | "max" | "target"): string {
  if (direction === "min") return "↓";
  if (direction === "max") return "↑";
  return "→";
}

function fmtMetric(value: number | null, digits = 3): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toFixed(digits);
}

function fmtMm(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)} mm`;
}

function fitFailureLabel(row: GuardrailPerCorner): string {
  const failed = [
    row.r2_dyn_pass ? null : "R²",
    row.delta_mean_pass ? null : "Δmean",
  ].filter(Boolean);
  return failed.length ? failed.join("+") : "ok";
}

function statusClass(status: ProvenanceInputStatus["status"]): string {
  return status === "PASS" ? "ok" : "warn";
}

export function StudyDefinitionRail(
  { test, evaluation, inputs: studyInputs }: StudyDefinitionRailProps,
) {
  const trusted = test.provenance?.trusted ?? false;
  const provInputs = test.provenance ? Object.values(test.provenance.inputs) : [];
  const passCount = provInputs.filter((v) => v.status === "PASS").length;
  const passText = test.provenance ? ` (${passCount}/${provInputs.length} PASS)` : "";

  // Slice: four-input audit detail from the Reference run (null until R0 done).
  const detail = studyInputs?.status === "ready" ? studyInputs.inputs : null;

  const objective = evaluation?.objective ?? null;

  // Guardrail verdict (output fidelity) is read off the Reference run
  // (run_number 0) — distinct from the input-trust line above.
  const referenceRun = test.runs.find((r) => r.run_number === 0) ?? null;
  const refGuardrails =
    referenceRun != null
      ? evaluation?.runs[String(referenceRun.id)]?.guardrails ?? null
      : null;
  // Counts run over EVALUABLE corners only — a replay corner with no
  // recorded truth contributes no gating check and must not be counted
  // as a pass or a fail (Study #6, 2026-06-11).
  const evaluableCorners = refGuardrails
    ? refGuardrails.per_corner.filter((c) => c.evaluable)
    : [];
  const cornerPassCount = evaluableCorners.filter(
    (c) => c.r2_dyn_pass && c.delta_mean_pass,
  ).length;
  const cornerTotal = evaluableCorners.length;
  const refVerdict = refGuardrails ? guardrailVerdict(refGuardrails) : null;

  // Slice 3: read-only tyre override (chosen at create). When the Reference
  // run actually used it as a counterfactual, flag it (fidelity informational).
  const tyre = tireOverrideOf(test);
  const refCounterfactual =
    referenceRun != null
      ? Boolean(
          evaluation?.runs[String(referenceRun.id)]?.counterfactual_tyre
            ?.counterfactual,
        )
      : false;

  return (
    <aside className="study-band" data-testid="study-rail">
      <span className="study-band-heading">Study definition</span>
      <div className="study-band-cards">
      <InputCard index={1} title="Track input" testid="study-card-actuation"
        value={<><strong>{test.source_event ?? (setupFileOf(test) ? `${trackOf(test).toUpperCase()} lap replay` : "(unset)")}</strong><div className="muted">{trackOf(test)}</div><div className="muted" data-testid="study-actuation-requested">requested: {requestedActuationLabel(test)}</div></>}
        affordance={<span className="study-tag">event</span>}
        detail={detail ? detailBlock(rigRows(detail), "rig-detail") : null} />

      <InputCard index={2} title="Car platform" testid="study-card-platform"
        value={<><strong>{test.baseline_setup_name
          ?? (setupFileOf(test) ? setupFileLabel(setupFileOf(test)!) : "(from event)")}</strong></>}
        affordance={<span className="study-tag">{setupFileOf(test) ? "own setup" : "from event"}</span>}
        detail={detail ? detailBlock(platformRows(detail), "platform-detail") : null} />

      <InputCard index={3} title="Tyre sidewall" testid="study-card-tyre"
        value={
          tyre ? (
            <>
              <strong>{tyre.type}</strong>
              {refCounterfactual && (
                <span
                  className="study-tag warn"
                  data-testid="study-tyre-counterfactual"
                >
                  {" "}counterfactual
                </span>
              )}
            </>
          ) : (
            <span className="muted">event-derived default</span>
          )
        }
        affordance={<span className="study-tag">from event</span>}
        detail={detail ? detailBlock(tyreRows(detail), "tyre-detail") : null} />

      <InputCard index={4} title="Damper config" testid="study-card-damper"
        value={<span className="muted">PVP clicks · F-V overlay (experimental)</span>}
        affordance={<span className="study-tag">edit ✎</span>}
        detail={detail ? detailBlock(damperRows(detail), "damper-detail") : null} />
      </div>

      <div className="study-band-eval">
      <div className="study-objective" data-testid="study-objective">
        <span className="study-panel-label">Objective</span>
        {objective ? (
          <span className="study-objective-value" data-testid="study-objective-name">
            <span
              className="study-objective-dir"
              data-testid="study-objective-direction"
              title={`optimise: ${objective.direction}`}
            >
              {directionMarker(objective.direction)}
            </span>{" "}
            {objective.display_name}
          </span>
        ) : (
          <span className="muted">choose after the reference run</span>
        )}
      </div>

      <div className="study-guardrails" data-testid="study-guardrails">
        <span className="study-panel-label">Guardrails</span>
        <div className="study-trust-axis" data-testid="study-physics-fit">
          <span className="study-trust-axis-label">Physics fit</span>
          {refGuardrails ? (
            <span className="study-guardrail-verdict" data-testid="study-guardrail-verdict">
              <span
                className={
                  refVerdict === "PASS"
                    ? "study-trust ok"
                    : refVerdict === "FAIL"
                      ? "study-trust warn"
                      : "study-trust na"
                }
                data-testid="study-guardrail-overall"
              >
                {refVerdict === "NOT_EVALUABLE" ? "N/A" : refVerdict}
              </span>
              <span className="muted" data-testid="study-guardrail-corners">
                {refVerdict === "NOT_EVALUABLE"
                  ? " no recorded truth (replay)"
                  : ` ${cornerPassCount}/${cornerTotal} corners`}
              </span>
            </span>
          ) : (
            <span className="muted">awaiting reference run</span>
          )}
        </div>
        {refGuardrails ? (
          <details
            className="study-diagnostic-disclosure"
            data-testid="study-physics-fit-detail"
          >
            <summary>fit detail</summary>
            <div className="study-fit-grid">
              <span>corner</span>
              <span>R²_dyn</span>
              <span>Δmean</span>
              <span>RMSE</span>
              <span>verdict</span>
              {refGuardrails.per_corner.map((row) => {
                const pass = row.r2_dyn_pass && row.delta_mean_pass;
                const cls = !row.evaluable ? "na" : pass ? "ok" : "warn";
                return (
                  <div
                    className={`study-fit-row ${cls}`}
                    key={row.corner}
                    data-testid={`study-fit-${row.corner}`}
                  >
                    <span>{row.corner.toUpperCase()}</span>
                    <span>{fmtMetric(row.r2_dyn)}</span>
                    <span>{fmtMm(row.delta_mean_mm)}</span>
                    <span>{fmtMetric(row.rmse_m, 4)} m</span>
                    <span className={`study-trust ${cls}`}>
                      {!row.evaluable
                        ? "N/A"
                        : pass
                          ? "PASS"
                          : `FAIL ${fitFailureLabel(row)}`}
                    </span>
                  </div>
                );
              })}
            </div>
          </details>
        ) : null}
        <div className="study-trust-axis" data-testid="study-input-provenance">
          <span className="study-trust-axis-label">Input provenance</span>
          <span className={trusted ? "study-trust ok" : "study-trust warn"}>
            {trusted ? "TRUSTED" : "CHECK"}{passText}
          </span>
        </div>
        {test.provenance ? (
          <details
            className="study-diagnostic-disclosure"
            data-testid="study-input-provenance-detail"
          >
            <summary>provenance detail</summary>
            <div className="study-provenance-list">
              {Object.entries(test.provenance.inputs).map(([name, item]) => (
                <div className="study-provenance-row" key={name}>
                  <span className="study-provenance-name">{name}</span>
                  <span className={`study-trust ${statusClass(item.status)}`}>
                    {item.status}
                  </span>
                  <span className="study-provenance-source">
                    {item.reason || item.source || "source unavailable"}
                  </span>
                </div>
              ))}
            </div>
          </details>
        ) : null}
        <p className="study-trust-caption">
          Input provenance checks source authority; physics fit checks waveform
          fidelity.
        </p>
      </div>
      </div>
    </aside>
  );
}
